import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { OfficeProfileResult } from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { getOfficeProfiles } from "@/requests/administration/administrationOffices";
import { OfficeFormOfPayment } from "@/models/api/administration/offices/OfficeFormOfPayment";
import { OfficeProfileFormOfPayment } from "@/models/api/administration/offices/OfficeProfileFormOfPayment";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { OfficeFormOfPaymentByType } from "@/models/api/administration/offices/OfficeFormOfPaymentByType";
import { FORM_OF_PAYMENTS } from "@/static/formOfPayments";
import { FORM_OF_PAYMENT_TYPE } from "@/static/formOfPaymentType";
import {
  getFormOfPaymentByOfficeProfileId,
  assignFormOfPaymentToOfficeProfile,
  unassignFormOfPaymentToOfficeProfile,
  getFormOfPaymentsByType,
  getFormOfPaymentsAll,
  setValueCanIssueReceipt,
  setValueCanIssueReceiptIndividualContractor,
  postFormOfPaymentTypeUpdate,
} from "@/requests/administration/administrationPayments";
export interface AdministrationPaymentMethodsStateInterface {
  //Table office profiles
  officeProfiles: Array<OfficeProfileResult>;
  officeProfileSelected: OfficeProfileResult | null;
  formOfPayments: Array<OfficeFormOfPayment>;
  officeProfileFormOfPayment: Array<OfficeProfileFormOfPayment>;
  recordStampId: number | null;
  selectedPaymentMethodId: FORM_OF_PAYMENTS | null;
  selectedPaymentMethod: OfficeProfileFormOfPayment | null;
  formOfPaymentsByType: Array<OfficeFormOfPaymentByType>;
}

export const AdministrationPaymentMethodsState: AdministrationPaymentMethodsStateInterface = {
  officeProfiles: [],
  officeProfileSelected: null,
  formOfPayments: [],
  officeProfileFormOfPayment: [],
  recordStampId: null,
  selectedPaymentMethodId: null,
  selectedPaymentMethod: null,
  formOfPaymentsByType: [],
};

const AdministrationPaymentMethodsModule = defineModule({
  state: AdministrationPaymentMethodsState as AdministrationPaymentMethodsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setOfficeProfile(state, param: Array<OfficeProfileResult>) {
      state.officeProfiles = param;
    },
    setSelectedOfficeProfile(state, param: OfficeProfileResult) {
      state.officeProfileSelected = param;
    },
    setFormOfPayments(state, param: Array<OfficeFormOfPayment>) {
      state.formOfPayments = param;
    },
    setFormOfPaymentsByType(state, param: Array<OfficeFormOfPaymentByType>) {
      state.formOfPaymentsByType = param;
    },
    setOfficeProfileFormOfPayment(state, param: Array<OfficeProfileFormOfPayment>) {
      state.officeProfileFormOfPayment = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setSelectedPaymentMethodId(state, param: number | null) {
      state.selectedPaymentMethodId = param;
    },
    setSelectedPaymentMethod(state, param: OfficeProfileFormOfPayment) {
      state.selectedPaymentMethod = param;
    },
  },
  actions: {
    selectPaymentMethod(context, param: OfficeProfileFormOfPayment) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedPaymentMethod(param);
      commit.setSelectedPaymentMethodId(param.id);
    },
    async formOfPaymentTypeUpdate(
      context,
      param: {
        formOfPaymentType: FORM_OF_PAYMENT_TYPE;
        value: boolean;
      }
    ) {
      const { rootDispatch, state } = _moduleActionContext(context);
      if (state.selectedPaymentMethodId != null) {
        await postFormOfPaymentTypeUpdate(state.selectedPaymentMethodId, param.formOfPaymentType, param.value);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async setValueCanIssueReceiptIndividualContractor(
      context,
      param: { formOfPayment: FORM_OF_PAYMENTS; value: boolean }
    ) {
      const { rootDispatch } = _moduleActionContext(context);
      await setValueCanIssueReceiptIndividualContractor(param.formOfPayment, param.value);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async setValueCanIssueReceipt(context, param: { formOfPayment: FORM_OF_PAYMENTS; value: boolean }) {
      const { rootDispatch } = _moduleActionContext(context);
      await setValueCanIssueReceipt(param.formOfPayment, param.value);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async downloadOfficeFormOfPaymentByType(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedPaymentMethodId != null) {
        const forms = await getFormOfPaymentsByType(state.selectedPaymentMethodId);
        commit.setFormOfPaymentsByType(forms);
      }
    },
    async downloadOfficeProfileFormOfPayment(context) {
      const { commit } = _moduleActionContext(context);
      const allFormsOfPayment = await getFormOfPaymentsAll();
      commit.setOfficeProfileFormOfPayment(allFormsOfPayment);
    },
    async downloadOffices(context) {
      const { commit } = _moduleActionContext(context);
      const offices = await getOfficeProfiles();
      commit.setOfficeProfile(offices);
    },
    async getFormOfPaymentByOfficeId(context) {
      const { state, commit, rootDispatch } = _moduleActionContext(context);
      if (state.officeProfileSelected != null) {
        const formOfPayment = await getFormOfPaymentByOfficeProfileId(state.officeProfileSelected.id);
        commit.setFormOfPayments(formOfPayment);
      } else
        rootDispatch.popup.addSnackbar(
          new SnackbarModel(filterCapitalize(i18n.t("selectOfficeProfile").toString()), "error")
        );
    },
    async assignFormOfPaymentToOfficeProfile(context, paymentMethodId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.officeProfileSelected;
      if (officeProfile != null) {
        await assignFormOfPaymentToOfficeProfile(officeProfile.id, paymentMethodId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async unassignFormOfPaymentToOfficeProfile(context, paymentMethodId: number) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const officeProfile = state.officeProfileSelected;
      if (officeProfile != null) {
        await unassignFormOfPaymentToOfficeProfile(officeProfile.id, paymentMethodId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
  },
});

export default AdministrationPaymentMethodsModule;
// eslint-disable-next-line
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationPaymentMethodsModule);
